import { NumberField as BaseNumberField } from '@base-ui/react/number-field';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import type { SxProps, Theme } from '@mui/material/styles';
import * as React from 'react';
import { getDecimalSeparator, normalizeTypedDecimal } from './number-field-decimal';

function SSRInitialFilled(_: BaseNumberField.Root.Props) {
  return null;
}
SSRInitialFilled.muiName = 'Input';

interface NumberFieldProps {
  label?: React.ReactNode;
  value?: number | null;
  defaultValue?: number;
  onValueChange?: (
    value: number | null,
    eventDetails: BaseNumberField.Root.ChangeEventDetails,
  ) => void;
  min?: number;
  max?: number;
  step?: number;
  smallStep?: number;
  largeStep?: number;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  autoFocus?: boolean;
  sx?: SxProps<Theme>;
  allowWheelScrub?: boolean;
  id?: string;
  /**
   * Replaces the default increment/decrement stepper. Use it when the value's
   * unit belongs inside the field (a measure) rather than beside it: the field
   * then reads as one object instead of a number plus a separate control.
   */
  endAdornment?: React.ReactNode;
  /**
   * Renders a read-only measurement unit in the shared, padded end slot.
   * Use `endAdornment` instead when the slot needs to be interactive.
   */
  unit?: React.ReactNode;
  /**
   * Number formatting and parsing locale. Defaults to the runtime locale.
   */
  locale?: Intl.LocalesArgument;
  /**
   * Extra `id`s (space-separated) joined to the input's `aria-describedby`,
   * alongside the helper text. Use it when the visible context that explains
   * the field (a heading, a caption) lives outside the form control.
   */
  describedBy?: string;
}

export function NumberField({
  label,
  value,
  defaultValue,
  onValueChange,
  min,
  max,
  step,
  smallStep,
  largeStep,
  size = 'medium',
  error,
  helperText,
  disabled,
  required,
  fullWidth,
  autoFocus,
  sx,
  allowWheelScrub,
  id: idProp,
  endAdornment,
  unit,
  locale,
  describedBy,
}: NumberFieldProps) {
  let id = React.useId();
  if (idProp) {
    id = idProp;
  }

  const decimalSeparator = React.useMemo(() => getDecimalSeparator(locale), [locale]);

  // Base UI parses by locale and offers no parse hook, so a typed `.` reaches
  // it as a grouping separator in it-IT: "20.0" becomes 200, which `max` then
  // clamps to its bound. Rewrite the separator before Base UI sees the event.
  const normalizeDecimalInput = React.useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      baseOnChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const normalized = normalizeTypedDecimal(event.target.value, decimalSeparator);
      if (normalized !== event.target.value) {
        event.target.value = normalized;
      }
      baseOnChange?.(event);
    },
    [decimalSeparator],
  );

  return (
    <BaseNumberField.Root
      value={value ?? null}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      locale={locale}
      min={min}
      max={max}
      step={step}
      smallStep={smallStep}
      largeStep={largeStep}
      disabled={disabled}
      required={required}
      allowWheelScrub={allowWheelScrub}
      render={(props, state) => (
        <FormControl
          size={size}
          ref={props.ref}
          disabled={state.disabled}
          required={state.required}
          error={error}
          variant="outlined"
          fullWidth={fullWidth}
          sx={sx}
        >
          {props.children}
        </FormControl>
      )}
    >
      <SSRInitialFilled value={value ?? null} />
      {label && <InputLabel htmlFor={id}>{label}</InputLabel>}
      <BaseNumberField.Input
        id={id}
        render={(props, state) => (
          <OutlinedInput
            aria-describedby={
              [describedBy, helperText ? `${id}-helper-text` : undefined]
                .filter(Boolean)
                .join(' ') || undefined
            }
            error={error}
            label={label}
            inputRef={props.ref}
            value={state.inputValue}
            onBlur={props.onBlur}
            onChange={(event) => normalizeDecimalInput(event, props.onChange)}
            onKeyUp={props.onKeyUp}
            onKeyDown={props.onKeyDown}
            onFocus={props.onFocus}
            autoFocus={autoFocus}
            slotProps={{
              input: {
                ...props,
                // Applied after the spread on purpose: `props.onChange` is the
                // raw Base UI handler and would otherwise win on the inner
                // input, bypassing the separator rewrite above.
                onChange: (event) => normalizeDecimalInput(event, props.onChange),
                'aria-invalid': error || undefined,
                style: { fontSize: '16px', ...props.style },
              },
            }}
            endAdornment={
              endAdornment ??
              (unit !== undefined ? (
                <InputAdornment
                  position="end"
                  sx={{
                    alignSelf: 'stretch',
                    maxHeight: 'unset',
                    ml: 0,
                    px: 1.75,
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    color: 'text.secondary',
                    fontSize: '1rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {unit}
                </InputAdornment>
              ) : (
                <InputAdornment
                  position="end"
                  sx={{
                    flexDirection: 'column',
                    maxHeight: 'unset',
                    alignSelf: 'stretch',
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    ml: 0,
                    '& button': {
                      py: 0,
                      flex: 1,
                      borderRadius: 0.5,
                    },
                  }}
                >
                  <BaseNumberField.Increment
                    render={<IconButton size={size} aria-label="Increase" tabIndex={-1} />}
                  >
                    <KeyboardArrowUpIcon fontSize={size} sx={{ transform: 'translateY(2px)' }} />
                  </BaseNumberField.Increment>
                  <BaseNumberField.Decrement
                    render={<IconButton size={size} aria-label="Decrease" tabIndex={-1} />}
                  >
                    <KeyboardArrowDownIcon fontSize={size} sx={{ transform: 'translateY(-2px)' }} />
                  </BaseNumberField.Decrement>
                </InputAdornment>
              ))
            }
            sx={{
              pr: 0,
              '& .MuiOutlinedInput-input': { fontVariantNumeric: 'tabular-nums' },
            }}
          />
        )}
      />
      {/* La live region resta sempre nel DOM: una regione che nasce già piena
          non viene annunciata dalla maggior parte degli screen reader. */}
      <FormHelperText
        id={`${id}-helper-text`}
        sx={{ ml: 0, display: helperText ? undefined : 'none' }}
        aria-live="polite"
      >
        {helperText}
      </FormHelperText>
    </BaseNumberField.Root>
  );
}
